import { APP_ROUTES, ADMIN_ROUTES } from '@/config/routes';
import { API_BASE_URL } from '@/lib/config/constants';
import { SessionManager } from './SessionManager';

/**
 * Per-domain SessionManager singletons. Phase 2 wires these into the
 * ApiClient instances (userApiClient / adminApiClient) and the
 * AdminAuthContext + authService.
 *
 * Endpoints are prefixed with API_BASE_URL: SessionManager fetches these
 * directly (not via ApiClient), so without the prefix they resolve against the
 * page origin. In split-host deploys (frontend on app.*, API on api.*) that
 * hits the frontend host, which serves index.html for /api/* — the boot probe
 * then sees HTML instead of a session and logs the user out on every refresh.
 * API_BASE_URL is empty in dev (same-origin via the Vite proxy), so the paths
 * stay relative there.
 */
export const userSession = new SessionManager({
  domain: 'user',
  probeEndpoint: `${API_BASE_URL}/api/v1/auth/me`,
  refreshEndpoint: `${API_BASE_URL}/api/v1/auth/secure/refresh`,
  loginRedirectUrl: APP_ROUTES.LOGIN,
});

export const adminSession = new SessionManager({
  domain: 'admin',
  probeEndpoint: `${API_BASE_URL}/api/v1/admin/auth/me`,
  refreshEndpoint: `${API_BASE_URL}/api/v1/admin/auth/refresh`,
  loginRedirectUrl: ADMIN_ROUTES.LOGIN,
  // /admin/auth/me uses get_optional_admin and returns 200 with
  // `{authenticated: false}` for unauthenticated callers. Without this
  // predicate, probe would treat that response as `alive` and any
  // subsequent admin-required request would 401, trip the anomaly
  // refresh path against an empty refresh cookie, and pop the
  // session_expired modal on the admin login page.
  isAliveFromBody: (body) =>
    body !== null &&
    typeof body === 'object' &&
    (body as { authenticated?: boolean }).authenticated === true,
});
